import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import type { SuggestionGains } from "@/client/pages/coach/optimize/coach-attributed-score";
import type { SkillAddition } from "@/client/pages/coach/optimize/coach-skill-additions";
import type { OptimizationSuggestion } from "@/client/pages/dashboard/ai-optimization/review";

/**
 * State of an in-progress Coach Resume optimization.
 *
 * Persisted to sessionStorage rather than held in memory because the flow spans a
 * full navigation (intake screen → builder) and a student who refreshes the builder
 * would otherwise lose every suggestion and have to pay for the LLM call again.
 * sessionStorage, not localStorage: this is scoped to one tab and one job, and it
 * must not leak into a later unrelated visit.
 *
 * Docs: lms-backend/docs/plans/2026-09-23-coach-resume-improvement.md
 */
export type CoachSessionSnapshot = {
  jobUuid: string;
  jobDescription?: string;
  jobTitle: string;
  companyName: string;
  returnOrigin: string;
  baselineScore: number;
  baselineBadge: string | null;
  baselineRoleDomainFit: number;
  baselineSkillOverlap: number;
  fixedContribution: number;
  weights: Record<string, number>;
  projectedCeiling: number;
  missingSkills: string[];
  presentSkills: string[];
};

type CoachOptimizationState = {
  /** Null whenever the builder is NOT in coach optimization mode. */
  session: CoachSessionSnapshot | null;
  /** Resume being optimized — cloned from the base resume at the intake screen. */
  resumeId: string | null;
  suggestions: OptimizationSuggestion[];
  /** Suggestion ids the student accepted. */
  acceptedIds: string[];
  /** Per-suggestion edited replacement text, keyed by suggestion id. */
  drafts: Record<string, string>;
  /**
   * The résumé's own wording at the start of this run, keyed by fieldPath.
   *
   * Reject restores from here rather than from `suggestion.previousText`, which is
   * re-baselined every run and therefore carries the PREVIOUS run's rewrite once one has
   * been accepted and saved. See pages/coach/optimize/original-content.ts.
   */
  originalValues: Record<string, string>;
  /**
   * What each suggestion is worth, keyed by suggestion id: the component it improves and the
   * sub-score points it adds.
   *
   * This is what makes "accepting increases the score as per the accepted changes" possible.
   * Without it the panel could only re-score the whole résumé and hope the number moved — which
   * is non-deterministic, gives no reason why a click helped, and cannot show a projection before
   * anything is accepted. See pages/coach/optimize/coach-attributed-score.ts.
   */
  gains: SuggestionGains;
  /**
   * Skills the résumé evidences but does not list, offered as accept/reject cards.
   *
   * Separate from `suggestions` because every suggestion is "replace the text at this fieldPath",
   * which cannot express "append an item to the skills list" — and adding a skill is the strongest
   * lever on `skillOverlap`. See pages/coach/optimize/coach-skill-additions.ts.
   */
  skillAdditions: SkillAddition[];
  /** Set once the resume has been bound, so the success modal is not shown twice. */
  confirmed: boolean;
  /**
   * Set once the student has dismissed the "don't close this tab" modal for this run.
   *
   * Lives in the store rather than in component state so it survives the navigation from the intake
   * screen to the builder and a refresh of the builder itself — the modal is a one-time orientation,
   * and re-showing it on every remount would train the student to dismiss it unread.
   *
   * Scoped to the session (sessionStorage, cleared by `start` and `reset`), so the next
   * optimization run warns again.
   */
  unloadNoticeAcknowledged: boolean;

  start: (payload: {
    session: CoachSessionSnapshot;
    resumeId: string;
    suggestions: OptimizationSuggestion[];
    originalValues?: Record<string, string>;
    gains?: SuggestionGains;
    skillAdditions?: SkillAddition[];
  }) => void;
  acceptSuggestion: (id: string) => void;
  rejectSuggestion: (id: string) => void;
  setDraft: (id: string, text: string) => void;
  markConfirmed: () => void;
  acknowledgeUnloadNotice: () => void;
  reset: () => void;
};

export const useCoachOptimizationStore = create<CoachOptimizationState>()(
  persist(
    (set) => ({
      session: null,
      resumeId: null,
      suggestions: [],
      acceptedIds: [],
      drafts: {},
      originalValues: {},
      gains: {},
      skillAdditions: [],
      confirmed: false,
      unloadNoticeAcknowledged: false,

      start: ({
        session,
        resumeId,
        suggestions,
        originalValues = {},
        gains = {},
        skillAdditions = [],
      }) =>
        set({
          session,
          resumeId,
          suggestions,
          gains,
          skillAdditions,
          // Captured before any accept, so reject always has the student's own wording to
          // put back even after an earlier run overwrote the résumé.
          originalValues,
          // A fresh run starts with nothing accepted: the student opts in to each
          // change, so the projected score must begin at the baseline.
          acceptedIds: [],
          drafts: {},
          confirmed: false,
          // A new run warns again: the student is about to spend another session's work on a
          // handoff that closing the tab would discard.
          unloadNoticeAcknowledged: false,
        }),

      acceptSuggestion: (id) =>
        set((state) => ({
          acceptedIds: state.acceptedIds.includes(id)
            ? state.acceptedIds
            : [...state.acceptedIds, id],
        })),

      rejectSuggestion: (id) =>
        set((state) => ({
          acceptedIds: state.acceptedIds.filter((acceptedId) => acceptedId !== id),
        })),

      setDraft: (id, text) => set((state) => ({ drafts: { ...state.drafts, [id]: text } })),

      markConfirmed: () => set({ confirmed: true }),

      acknowledgeUnloadNotice: () => set({ unloadNoticeAcknowledged: true }),

      reset: () =>
        set({
          session: null,
          resumeId: null,
          suggestions: [],
          acceptedIds: [],
          drafts: {},
          originalValues: {},
          gains: {},
          skillAdditions: [],
          confirmed: false,
          unloadNoticeAcknowledged: false,
        }),
    }),
    {
      name: "coach-optimization",
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);

/**
 * Is the builder currently showing a coach optimization for this resume?
 *
 * Checked by resume id as well as session presence so an abandoned session cannot
 * bleed suggestions into an unrelated resume the student opens later.
 */
export const useIsCoachOptimizing = (resumeId?: string) =>
  useCoachOptimizationStore(
    (state) => Boolean(state.session) && Boolean(resumeId) && state.resumeId === resumeId,
  );

// `useCoachScoreTracking` is gone. It existed to let the dashboard flow hide the score panel, on the
// grounds that it had no authoritative baseline — which made one feature look like two different
// screens. That flow now supplies the model's own assessment as its baseline (see
// buildDashboardCoachSession), so every session has a score and the panel is one implementation.

/**
 * Did this optimization come from the student side — i.e. is there an LMS job behind it?
 *
 * Derived from `jobUuid` rather than carried as its own flag, so it cannot disagree with the session it
 * describes: only the LMS handoff has a job, and `buildDashboardCoachSession` leaves it empty. An
 * earlier `scoreTracking` boolean was exactly that duplicate-truth mistake, and it was deleted.
 *
 * FALSE changes three things in the builder, because they only make sense against a real job score:
 *   - the Confirm bar is not rendered at all — there is no Career Coach tab to hand anything back to,
 *     and the résumé autosaves, so the button would be a no-op with a misleading label.
 *   - the ceiling explanation ("going higher needs experience this résumé doesn't show") is hidden. It
 *     describes the fixed half of a job-match score — compensation, location, experience band — and
 *     none of that is part of the résumé-fit score this flow shows.
 *   - the tier label under the percentage is hidden. "Stretch Role" is a verdict on a student against a
 *     job, not on a résumé against a job description.
 */
export const useCoachHasJobContext = () =>
  useCoachOptimizationStore((state) => Boolean(state.session?.jobUuid));
